import React, { useEffect, useState, useRef } from 'react';
import { Menu, X, ArrowRight,  Code, Repeat, Server, Instagram, Mail,  ShoppingCart, User, Briefcase,BarChart2 } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

// --- Global Data ---
const services = [
    {
      id: 'dev',
      title: 'Website Design and Development',
      description: 'At Vyxion Labs, we build websites tailored to your business — because no two companies, industries, or goals are the same. We take the time to understand your needs and customise every aspect of your site.',
      features: ['Custom Web Designs', 'Responsive Design', 'Security'],
      image: '/web_design.jpg',
      gradient: 'from-cyan-400 to-blue-500',
      cursorColor: 'rgba(34, 211, 238, 0.4)',
      hoverCursorColor: 'rgba(59, 130, 246, 0.6)',
      link: '/web-development',
      icon: Code,
    },
    {
      id: 'revamp',
      title: 'Website Revamp',
      description: 'Are you looking to give your website a fresh new look? Discover the benefits of our professional Website Revamp service. From enhanced functionality to improved user experience, we help you maximize your conversions.',
      features: ['Modern UI/UX Design', 'Performance Boost', 'Mobile Optimization', 'Brand Refresh'],
      image: '/web_revamp.jpg',
      gradient: 'from-purple-400 to-pink-500',
      cursorColor: 'rgba(168, 85, 247, 0.4)',
      hoverCursorColor: 'rgba(236, 72, 153, 0.6)',
      link: '/website-revamp',
      icon: Repeat,
    },
    {
      id: 'hosting',
      title: 'Server Hosting & Maintenance',
      description: 'Reliable, secure hosting solutions with 24/7 monitoring and expert maintenance to keep your site running flawlessly.',
      features: ['24/7 Server Monitoring', 'Security Updates', 'Content Revision', 'Technical Support'],
      image: '/server_hosting.jpg',
      gradient: 'from-emerald-400 to-teal-500',
      cursorColor: 'rgba(52, 211, 153, 0.4)',
      hoverCursorColor: 'rgba(20, 184, 166, 0.6)',
      link: '/hosting-maintenance',
      icon: Server,
    },
  ];


const CTABannerSection = ({ visible }) => {
  return (
    <section className="relative py-20 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div
          id="cta-banner"
          data-observe
          className={`flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-0 p-10 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-purple-500/10 shadow-xl border border-slate-200 transition-all duration-1000 ease-out ${
            visible['cta-banner'] ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-20"
          }`}
        >
          <div className={`lg:w-1/2 text-center lg:text-left space-y-4 transition-all duration-1000 delay-300 ${visible['cta-banner'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <p className="text-lg md:text-xl font-medium text-neutral-700">Got an idea you're excited about?</p>
            <h2 className="text-3xl md:text-6xl font-bold leading-tight text-neutral-900">
              Let's bring it to life. <br />
              <span className="font-serif italic text-cyan-600">All it takes is a conversation!</span>
            </h2>
            <a href="/consult" className="inline-flex items-center gap-2 px-8 py-3 bg-cyan-500 text-white font-bold rounded-xl shadow-lg hover:bg-cyan-600 transition-all hover:scale-105 mt-4">
              Start the Conversation <ArrowRight className="w-5 h-5" />
            </a>
          </div>
          <div className={`lg:w-1/2 text-center lg:text-right space-y-3 font-semibold transition-all duration-1000 delay-500 ${visible['cta-banner'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="text-2xl md:text-4xl font-bold text-neutral-900">For enquiries, email us:</div>
            <div className="text-lg md:text-2xl text-neutral-700 break-all">vyxionlabs.info@gmail.com</div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FooterSection = () => {
  return (
      <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-16 px-6">
          <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-12 border-b border-slate-700 pb-12">
                  <div className="col-span-2 md:col-span-2 space-y-6">
                      <a href="/" className="flex items-center gap-3">
                          <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                          <div>
                              <div className="text-xl font-bold text-white">Vyxion Labs</div>
                              <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                          </div>
                      </a>
                      <p className="text-sm text-slate-400 max-w-xs">
                          We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                      </p>
                      <div className="flex space-x-4 pt-2">
                        <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                        <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                      </div>
                  </div>
                  <div className="space-y-4">
                      <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                      <ul className="space-y-3">
                          <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                          <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                          <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                          <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                          <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                      </ul>
                  </div>
                  <div className="space-y-4">
                      <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                      <ul className="space-y-3">
                          {services.map((s, index) => (
                              <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                          ))}
                      </ul>
                  </div>
                  <div className="space-y-4 col-span-2 md:col-span-1">
                      <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                      <ul className="space-y-3">
                          <li className="flex items-center gap-3">
                              <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                              <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors">vyxionlabs.info@gmail.com</a>
                          </li>
                      </ul>
                  </div>
              </div>
              <div className="flex justify-center md:justify-start space-x-6 mt-8">
                  
                  <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
              </div>
              <div className="mt-4 text-center text-sm text-slate-500">
                  &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
              </div>
          </div>
      </footer>
  );
};

const WebsiteTypesSection = ({ visible }) => {
    const websiteTypes = [
        { icon: BarChart2, title: 'Landing Pages', description: 'Single-page sites engineered for maximum lead generation or sales conversions.', gradient: 'from-orange-400 to-red-500', imagePath: '/landingpage.jpg' },
        { icon: Briefcase, title: 'Corporate Pages', description: 'Establish strong credibility with a multi-page site that clearly showcases your brand.', gradient: 'from-blue-500 to-cyan-500', imagePath: '/corporate.jpg' },
        { icon: ShoppingCart, title: 'Ecommerce/Restaurant', description: 'Full-featured online stores with secure payment integration and seamless shopping.', gradient: 'from-pink-500 to-purple-500', imagePath: '/ecommerce.jpg' },
        { icon: User, title: 'Portfolios', description: 'Visually stunning platforms to display your work, skills, and resume.', gradient: 'from-teal-500 to-emerald-500', imagePath: '/portfolio.jpg' },
    ];

    return (
        <section className="relative py-16 md:py-20 px-4 md:px-6"> 
            <div className="max-w-7xl mx-auto">
                <div id="website-types-intro" data-observe className={`${visible["website-types-intro"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"} text-center mb-16 transition-all duration-1000`}>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 text-neutral-900 tracking-tight">
                        Built For Every Business Goal
                    </h2>
                    <p className="text-lg md:text-xl text-neutral-600 leading-relaxed max-w-3xl mx-auto">
                        We design and develop custom websites perfectly tailored to meet your specific business objectives.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                    {websiteTypes.map((type, index) => (
                        <div
                            key={index}
                            id={`type-${index}`}
                            data-observe
                            className={`flex flex-col p-6 md:p-8 rounded-xl border border-neutral-200 bg-white shadow-lg transition-all duration-1000 ${
                                visible[`type-${index}`] ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-20 scale-95"
                            }`}
                            style={{ transitionDelay: `${index * 150}ms` }}
                        >
                            <div className="h-48 w-full mb-6 flex items-center justify-center bg-neutral-100 rounded-lg overflow-hidden border border-neutral-200 relative">
                                <img src={type.imagePath} alt={type.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
                            </div>
                            <div className='flex flex-col items-center text-center'>
                                <h3 className="text-2xl font-bold text-neutral-900 mb-2">{type.title}</h3>
                                <p className="text-neutral-600 text-sm md:text-base">{type.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default function WebDevelopmentPage() {
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  
  const [visible, setVisible] = useState({});
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [hoveredService, setHoveredService] = useState(null);
  const [isHoveringButton, setIsHoveringButton] = useState(false);
  const [cardRect, setCardRect] = useState(null); 
  const [cursorScale, setCursorScale] = useState(1); 
  
  const servicesDropdownRef = useRef(null); 

  const handleServicesClick = (e) => {
    e.stopPropagation(); 
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [servicesDropdownRef]);

  const handleMenuClick = () => {
    setIsContactPopupOpen(!isContactPopupOpen);
  };

  const webDevServiceIndex = 0; 
  useEffect(() => {
    const handleMouseMove = (e) => {
      setCursorPos({ x: e.clientX, y: e.clientY });
      if (cardRect && hoveredService === webDevServiceIndex) {
        const x = e.clientX;
        const y = e.clientY;
        const centerX = cardRect.left + cardRect.width / 2;
        const centerY = cardRect.top + cardRect.height / 2;
        const distX = Math.abs(x - centerX);
        const distY = Math.abs(y - centerY);
        const normDistX = distX / (cardRect.width / 2);
        const normDistY = distY / (cardRect.height / 2);
        const maxNormDist = Math.max(normDistX, normDistY);
        const maxScale = 1.5; 
        const minScale = 1;   
        const proximity = 1 - Math.min(1, maxNormDist);
        const newScale = minScale + (maxScale - minScale) * proximity;
        setCursorScale(newScale);
      } else if (cursorScale !== 1) {
          setCursorScale(1); 
      }
    };
    
    if (window.matchMedia("(hover: hover)").matches) {
        window.addEventListener('mousemove', handleMouseMove);
    }
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [cardRect, hoveredService, cursorScale]); 

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setVisible((v) => ({ ...v, [e.target.id]: true }));
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-observe]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const webDevService = services[webDevServiceIndex];

  return (
    <div className="bg-white text-neutral-900 min-h-screen antialiased relative overflow-x-hidden font-inherit">
      <style>{`
        @keyframes float { 
          0%, 100% { transform: translateY(0px) rotate(0deg); } 
          50% { transform: translateY(-20px) rotate(5deg); } 
        }
        @keyframes slideInDown {
            0% { transform: translateY(-100%); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideInUp {
            0% { transform: translateY(50px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideInLeft {
            0% { transform: translateX(-100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInRight {
            0% { transform: translateX(100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
        }
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(8px);
            z-index: 9999; 
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .modal-overlay.open {
            opacity: 1;
            visibility: visible;
        }
        .modal-content {
            position: fixed;
            top: 0;
            right: 0;
            width: 100%;
            max-width: 400px;
            height: 100%;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(30px);
            box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
            transform: translateX(100%);
            transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);
            overflow-y: auto;
            padding: 2rem 1.5rem;
        }
        .modal-overlay.open .modal-content {
            transform: translateX(0);
        }
        .float-animation { animation: float 6s ease-in-out infinite; }
        .custom-cursor {
          pointer-events: none;
          position: fixed;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: all 0.15s ease-out; 
          z-index: 9999;
          mix-blend-mode: multiply;
        }
        .animate-slideInDown { animation: slideInDown 0.8s ease-out forwards; }
        .animate-slideInUp { animation: slideInUp 1s ease-out forwards; }
        .animate-slideInLeft { animation: slideInLeft 1s ease-out forwards; }
        .animate-slideInRight { animation: slideInRight 1s ease-out forwards; }
        .delay-100 { animation-delay: 100ms; }
        .delay-200 { animation-delay: 200ms; }
        .delay-300 { animation-delay: 300ms; }
      `}</style>

      {/* Custom Cursor */}
      {hoveredService === webDevServiceIndex && (
        <div
          className="custom-cursor hidden lg:block"
          style={{
            left: `${cursorPos.x}px`,
            top: `${cursorPos.y}px`,
            backgroundColor: isHoveringButton 
              ? webDevService.hoverCursorColor 
              : webDevService.cursorColor,
            transform: `translate(-50%, -50%) scale(${cursorScale * (isHoveringButton ? 1.3 : 1)})`,
            width: '60px',
            height: '60px',
          }}
        />
      )}

      {/* Header */}
      <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 z-50 opacity-100 fill-mode-forwards flex-shrink-0">
              <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
              </div>
              <div>
                <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
              </div>
            </a>

            {/* Nav - Center - Flexbox Layout */}
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8  opacity-100 fill-mode-forwards px-4" style={{animationFillMode: 'forwards'}}>
                <a href="/" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Home</a>
                <div className="relative" ref={servicesDropdownRef}>
                    <span id="services-link" className="text-neutral-900 font-bold cursor-pointer" onClick={handleServicesClick}>Services</span>
                    {isServicesDropdownOpen && (
                   <div id="services-dropdown" className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden z-[110]">
                   <div className="flex flex-col p-2">
                       <a href="/web-development" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm bg-neutral-100 transition-colors text-sm font-bold text-neutral-900">
                           Website Design & Development
                       </a>
                       <a href="/website-revamp" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Revamp
                       </a>
                       <a href="/hosting-maintenance" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Server Hosting & Maintenance
                       </a>
                   </div>
               </div>
               
                    )}
                </div>
                <a href="/pricing" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Pricing</a>
                <a href="/about" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">About Us</a>
                <a href="/ourworks" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium">Our Works</a>
                <a href="/faq" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">FAQ</a>
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-3 md:gap-4 opacity-100 fill-mode-forwards flex-shrink-0" style={{animationFillMode: 'forwards'}}>
                <a href="/consult" className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-900 transition-all text-sm shadow-sm">
                  <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">Free Consult <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </a>
                <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                    <a href="https://www.tiktok.com/@vyxion.labs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-black transition-colors"><FaTiktok className="w-4 h-4" /></a>
                    <a href="https://www.instagram.com/vyxionlabs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-pink-600 transition-colors"><Instagram className="w-4 h-4" /></a>
                </div>
                <button className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 hover:bg-neutral-100 transition-all text-sm bg-white ml-2" onClick={handleMenuClick}>
                    <Menu className="w-5 h-5" />
                </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Contact Overlay */}
      <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100 hover:bg-neutral-200 transition-colors" onClick={handleMenuClick}>
                <X size={24} className="text-neutral-700" />
            </button>
            <h2 className="text-3xl font-bold text-neutral-900 mb-8 mt-12 animate-slideInLeft">Navigation</h2>
            <div className="space-y-4 mb-10 border-b border-neutral-200 pb-8">
                <div className="space-y-3 pl-4 border-l-2 border-purple-200 animate-slideInLeft delay-100">
                     <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Services</p>
                     <a href="/web-development" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Web Development</a>
                     <a href="/website-revamp" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Website Revamp</a>
                     <a href="/hosting-maintenance" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Hosting</a>
                </div>
                <a href="/pricing" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Pricing</a>
                <a href="/" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Home</a>
                <a href="/faq" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-300" onClick={handleMenuClick}>FAQ</a>
                <a href="/about" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-400" onClick={handleMenuClick}>About Us</a>
                <a href="/consult" className="group relative block w-full text-center mt-12 px-6 py-4 bg-gray-800 text-gray-200 font-bold rounded-md hover:bg-gray-700 transition-all text-sm" onClick={handleMenuClick}>
              <span className="relative z-10 flex items-center justify-center gap-2">Start a Project Today <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
            </a>
            </div>
            <h3 className="text-2xl font-bold text-neutral-900 mb-4">Get in Touch</h3>
            <div className="space-y-4 mb-10">
                <div className="flex items-start gap-4 p-4 rounded-sm border border-neutral-200 bg-neutral-50">
                    <Mail size={24} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="text-lg font-bold text-neutral-800">Email</div>
                        <a href="mailto:vyxionlabs.info@gmail.com" className="text-neutral-600 hover:text-cyan-500 transition-colors text-base break-all">vyxionlabs.info@gmail.com</a>
                    </div>
                </div>
            </div>
           
        </div>
      </div>

      <main>
        {/* Title Section */}
        <section className="relative py-12 md:py-20 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight text-neutral-900 animate-slideInUp">
                Custom Website Design & Development
            </h1>
            <p className="text-lg md:text-xl text-neutral-600 mt-6 max-w-2xl text-left animate-slideInUp delay-100">
                Crafting stunning, functional websites engineered to achieve your business goals.
            </p>
            </div>
        </section>

        {/* Dedicated Service Section */}
        <section className="relative pt-0 pb-16 md:pb-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div
              id="webdev-details"
              data-observe
              className={`${visible["webdev-details"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"} transition-all duration-1000 flex flex-col lg:flex-row gap-8 md:gap-12 items-center`}
              onMouseEnter={(e) => {
                setHoveredService(webDevServiceIndex);
                setCardRect(e.currentTarget.getBoundingClientRect());
              }}
              onMouseLeave={() => {
                setHoveredService(null);
                setIsHoveringButton(false);
                setCardRect(null); 
                setCursorScale(1);
              }}
            >
                {/* Image Block */}
                <div className="w-full lg:w-1/2 relative animate-slideInLeft">
                    <div className="relative w-full aspect-video md:h-96 rounded-xl overflow-hidden shadow-2xl border-2 border-neutral-100 float-animation">
                        <img src="/web_design.jpg" alt={webDevService.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                </div>

                {/* Text Block */}
                <div className="w-full lg:w-1/2 flex flex-col space-y-6 text-center lg:text-left lg:items-start p-2 md:p-4 animate-slideInRight">
                    <h3 className="text-3xl md:text-4xl font-bold text-neutral-900">Your Vision, Our Expertise</h3>
                    <p className="text-neutral-600 leading-relaxed text-sm md:text-base">
                        <span className="md:hidden">{webDevService.description}</span>
                        <span className="hidden md:inline">
                          {webDevService.description} from structure to functionality. With clean, seamless interfaces and conversion-focused design, we create experiences that guide users to take action. Beyond design and development, we handle every detail of your website end-to-end, so it doesn’t just look good — it delivers real, measurable business results.
                        </span>
                    </p>
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full gap-6 lg:gap-0">
                        <ul className="space-y-2 w-full lg:w-auto flex flex-col items-center lg:items-start">
                            {webDevService.features.map((f, i) => (
                            <li key={i} className="flex items-center gap-2">
                                <div className={`w-2 h-2 bg-cyan-500 rounded-full`} />
                                <span className="text-neutral-700 font-medium">{f}</span>
                            </li>
                            ))}
                        </ul>
                        <a href="/consult" className="group relative px-6 py-3 bg-cyan-500 text-white font-bold rounded-md shadow-lg hover:bg-cyan-600 hover:scale-105 transition-all text-lg w-full sm:w-auto lg:ml-8" onMouseEnter={() => setIsHoveringButton(true)} onMouseLeave={() => setIsHoveringButton(false)}>
                            <span className="relative z-10 flex items-center justify-center gap-2">Build My Website <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
                        </a>
                    </div>
                </div>
            </div>
          </div>
        </section>

        <WebsiteTypesSection visible={visible} />
        <CTABannerSection visible={visible} />
        <FooterSection />

      </main>
    </div>
  );
}

